import type { JSX, ReactNode } from 'react';

/** The mono caption over a value: "PICKUP", "SPEED". */
export const caption = 'font-mono text-[10px] tracking-[.08em] text-mute';

/** One labelled value in a bordered tile — the load card's and the map's facts. */
export function Fact({ k, v }: { k: string; v: ReactNode }): JSX.Element {
  return (
    <div className="border-line bg-surf min-w-0 rounded-[10px] border p-3">
      <div className={caption}>{k.toUpperCase()}</div>
      <div className="mt-1 text-sm font-medium [overflow-wrap:anywhere]">{v}</div>
    </div>
  );
}

/** A status in an accent pill, as the load card shows it. */
export function StatusPill({ status }: { status: string }): JSX.Element {
  return (
    <span className="bg-acc-soft text-acc rounded-full px-2.5 py-1 text-xs font-medium">
      {status}
    </span>
  );
}

/** The line under a table or a document that says there is more than is shown. */
export function Truncated({ what }: { what: string }): JSX.Element {
  return <div className="text-mute mt-2.5 text-xs">Showing the first {what}; more matched.</div>;
}

/** Facts as label/value lines, for the panels with no room for tiles. */
export function FactLines({
  facts,
}: {
  facts: readonly { readonly k: string; readonly v: string }[];
}): JSX.Element | null {
  if (facts.length === 0) return null;
  return (
    <dl className="mt-2.5 flex flex-col gap-1">
      {facts.map((f, i) => (
        <div key={i} className="flex items-baseline justify-between gap-3">
          <dt className={`${caption} flex-none`}>{f.k.toUpperCase()}</dt>
          <dd className="m-0 min-w-0 text-right text-[12.5px] [overflow-wrap:anywhere]">{f.v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A number as the design sets numbers: monospace, tabular, right-aligned. */
export function Amount({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className={caption}>{label.toUpperCase()}</div>
      <div className="mt-[3px] truncate font-mono text-[13px] tabular-nums">{value}</div>
    </div>
  );
}
